import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiPath } from "../lib/url";
import type { ArticleSummary, MeResponse } from "../lib/types";

// Batch overview after login. Lists the five articles. Each card
// shows the working title, source, and the Q+A the freelancer is about
// to read. Clicking Record routes into the camera UI.
export default function Batch() {
  const navigate = useNavigate();
  const [data, setData] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(apiPath("/api/me"));
      if (res.status === 401) {
        navigate("/", { replace: true });
        return;
      }
      if (!res.ok) {
        setError(`Could not load batch (${res.status})`);
        setLoading(false);
        return;
      }
      const next = (await res.json()) as MeResponse;
      setData(next);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading) return <div className="batch-shell"><div className="loading">Loading…</div></div>;
  if (error) return <div className="batch-shell"><div className="errstate">{error}</div></div>;
  if (!data) return null;

  return (
    <div className="batch-shell">
      <div className="batch-topbar">
        <div className="batch-brand">
          <span className="mark" />
          Flywheel Studio
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {data.batch.freelancerUsername && (
            <span className="batch-who">{data.batch.freelancerUsername}</span>
          )}
          <Link to="/logout" className="btn ghost" style={{ padding: "8px 14px", fontSize: 13 }}>
            Sign out
          </Link>
        </div>
      </div>

      <h1 className="batch-title">{data.batch.name || "Your batch"}</h1>

      {data.articles.length === 0 && (
        <div className="loading">No articles in this batch yet.</div>
      )}

      {data.articles.map((a) => (
        <ArticleCard
          key={a.id}
          article={a}
          total={data.articles.length}
        />
      ))}
    </div>
  );
}

function ArticleCard({ article, total }: { article: ArticleSummary; total: number }) {
  const uploaded = !!article.rawVideoKey;
  const interview = article.interviewText ?? "";
  const segments = splitInterview(interview);

  return (
    <div className="article-card">
      <div className="article-card-header">
        <div style={{ flex: 1 }}>
          <div className="article-position">
            Video {article.position} of {total}
            {uploaded && (
              <>
                {" · "}
                <span className="status-pill done">uploaded</span>
              </>
            )}
          </div>
          <h2 className="article-title">
            {article.workingTitle || article.sourceTitle || "(untitled)"}
          </h2>
          {article.sourceTitle && article.workingTitle && (
            <p className="article-source">From: {article.sourceTitle}</p>
          )}
        </div>
      </div>

      {segments.length > 0 && (
        <div className="interview-block">
          <div className="interview-block-label">Script preview</div>
          {segments.slice(0, 2).map((seg, i) => (
            <div key={i}>
              <p className="interview-q">{seg.q}</p>
              <p className="interview-a">{seg.a}</p>
            </div>
          ))}
          {segments.length > 2 && (
            <p className="interview-more">
              + {segments.length - 2} more Q+A · open the article for the full script
            </p>
          )}
        </div>
      )}

      <div className="article-actions">
        <Link
          to={`/batch/record/${article.id}`}
          className={uploaded ? "btn ghost" : "btn primary"}
        >
          {uploaded ? "Replace upload" : "Open script + upload"}
        </Link>
        <span style={{ color: "var(--ink-3)", fontSize: 13.5 }}>
          {article.durationSeconds}s target
        </span>
      </div>
    </div>
  );
}

// Split an interview text into Q/A pairs. The script generator emits
// alternating "Q:" and "A:" lines, but we stay defensive: if the format
// looks off, fall back to a single A: with the whole body.
export interface QASegment {
  q: string;
  a: string;
}

export function splitInterview(text: string): QASegment[] {
  if (!text.trim()) return [];
  const lines = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const segs: QASegment[] = [];
  let currentQ: string | null = null;
  let currentA: string[] = [];
  const flush = () => {
    if (currentQ !== null || currentA.length > 0) {
      segs.push({ q: currentQ ?? "", a: currentA.join(" ") });
    }
    currentQ = null;
    currentA = [];
  };
  for (const line of lines) {
    const qMatch = line.match(/^Q:\s*(.*)$/i);
    const aMatch = line.match(/^A:\s*(.*)$/i);
    if (qMatch) {
      flush();
      currentQ = qMatch[1].trim();
    } else if (aMatch) {
      currentA.push(aMatch[1].trim());
    } else if (currentQ !== null || currentA.length > 0) {
      currentA.push(line);
    }
  }
  flush();
  if (segs.length === 0) {
    return [{ q: "Talk about this", a: text.trim() }];
  }
  return segs;
}
